import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  isVisible: boolean;
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

const Toast = ({ isVisible, message, type = 'info', onClose, duration = 3000 }: ToastProps) => {
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isVisible) {
      timer = setTimeout(onClose, duration);
    }
    return () => clearTimeout(timer);
  }, [isVisible, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <div className="flex-shrink-0 bg-gradient-to-br from-green-100 via-emerald-100 to-green-200 dark:from-green-900/30 dark:via-emerald-900/25 dark:to-green-800/30 border border-green-300 dark:border-green-600 rounded-full p-2">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="flex-shrink-0 bg-gradient-to-br from-red-100 via-rose-100 to-red-200 dark:from-red-900/30 dark:via-rose-900/25 dark:to-red-800/30 border border-red-300 dark:border-red-600 rounded-full p-2">
            <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="flex-shrink-0 bg-gradient-to-br from-yellow-100 via-amber-100 to-yellow-200 dark:from-yellow-900/30 dark:via-amber-900/25 dark:to-yellow-800/30 border border-yellow-300 dark:border-yellow-600 rounded-full p-2">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 bg-gradient-to-br from-blue-100 via-indigo-100 to-blue-200 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 border border-blue-300 dark:border-blue-600 rounded-full p-2">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-gradient-to-br from-green-50 via-emerald-50 to-green-100 dark:from-green-900/30 dark:via-emerald-900/25 dark:to-green-800/30 border border-green-300 dark:border-green-600 border-l-4 border-l-green-500';
      case 'error':
        return 'bg-gradient-to-br from-red-50 via-rose-50 to-red-100 dark:from-red-900/30 dark:via-rose-900/25 dark:to-red-800/30 border border-red-300 dark:border-red-600 border-l-4 border-l-red-500';
      case 'warning':
        return 'bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-100 dark:from-yellow-900/30 dark:via-amber-900/25 dark:to-yellow-800/30 border border-yellow-300 dark:border-yellow-600 border-l-4 border-l-yellow-500';
      default:
        return 'bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/25 dark:to-blue-800/30 border border-blue-300 dark:border-blue-600 border-l-4 border-l-blue-500';
    }
  };

  const getProgressBarColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      default:
        return 'bg-blue-500';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
          className="fixed top-4 left-4 right-4 lg:left-auto lg:right-4 lg:translate-x-0 lg:w-auto z-50"
        >
          <div className={`
            relative overflow-hidden
            flex items-center gap-3 px-4 py-3 rounded-lg
            ${getBackgroundColor()}
            shadow-md
            w-full lg:min-w-[320px] lg:max-w-md lg:w-auto
          `}>
              {getIcon()}

              <p className="text-sm font-medium text-gray-700 dark:text-gray-200 flex-1">
                {message}
              </p>

            <button
              onClick={onClose}
              className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: duration / 1000, ease: "linear" }}
              className={`absolute bottom-0 left-0 right-0 h-0.5 ${getProgressBarColor()} bg-opacity-50 rounded-b-lg`}
              style={{ transformOrigin: 'left' }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Toast; 